import {
  useRef,
  useEffect,
  useCallback,
  type ClipboardEvent,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { cn } from '@/lib/cn';

interface OtpInputProps {
  /** Current 0–6 digit string. */
  value: string;
  /** Fired with the new value on every change. */
  onChange: (next: string) => void;
  /** Fired when all six digits are filled — auto-submit hook. */
  onComplete?: (code: string) => void;
  /** Disable the entire input during a verify-in-flight. */
  disabled?: boolean;
  className?: string;
}

const LENGTH = 6;

/**
 * Six-digit code input — one box per digit, auto-advance forward, backspace
 * to retreat, paste handled cleanly. Feels like an OTP input should.
 *
 * Visual: ink boxes with pink active border, monospace digit + tabular nums
 * so the columns stay aligned across glyphs. Fires `onComplete` once the
 * sixth digit lands so the parent can verify without an extra button tap.
 */
export const OtpInput = ({
  value,
  onChange,
  onComplete,
  disabled = false,
  className,
}: OtpInputProps) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: LENGTH }).map((_, i) => value[i] ?? '');

  // Autofocus first empty box on mount.
  useEffect(() => {
    const firstEmpty = digits.findIndex((d) => d === '');
    const targetIdx = firstEmpty === -1 ? LENGTH - 1 : firstEmpty;
    refs.current[targetIdx]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire onComplete when the value reaches 6 digits.
  useEffect(() => {
    if (value.length === LENGTH && onComplete) {
      onComplete(value);
    }
  }, [value, onComplete]);

  const setDigitAt = useCallback(
    (idx: number, next: string) => {
      const arr = digits.slice();
      arr[idx] = next;
      const merged = arr.join('').slice(0, LENGTH);
      onChange(merged);
    },
    [digits, onChange],
  );

  const onChangeAt = (idx: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      setDigitAt(idx, '');
      return;
    }
    // If the user pasted multiple digits into one box, scatter them.
    if (raw.length > 1) {
      const chars = raw.slice(0, LENGTH - idx).split('');
      const arr = digits.slice();
      chars.forEach((c, i) => {
        if (idx + i < LENGTH) arr[idx + i] = c;
      });
      onChange(arr.join('').slice(0, LENGTH));
      const nextFocus = Math.min(idx + chars.length, LENGTH - 1);
      refs.current[nextFocus]?.focus();
      return;
    }
    setDigitAt(idx, raw[0]);
    if (idx < LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const onKeyDownAt = (idx: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
      setDigitAt(idx - 1, '');
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowRight' && idx < LENGTH - 1) {
      refs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const digitsOnly = text.replace(/\D/g, '').slice(0, LENGTH);
    if (digitsOnly) {
      e.preventDefault();
      onChange(digitsOnly);
      const nextFocus = Math.min(digitsOnly.length, LENGTH - 1);
      refs.current[nextFocus]?.focus();
    }
  };

  return (
    <div
      role="group"
      aria-label="6-digit code"
      className={cn('grid grid-cols-6 gap-2 sm:gap-3', className)}
    >
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={onChangeAt(idx)}
          onKeyDown={onKeyDownAt(idx)}
          onPaste={onPaste}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${idx + 1}`}
          className={cn(
            'h-14 w-full border bg-ink/40 text-center sm:h-16',
            'font-display text-2xl font-black tabular-nums sm:text-3xl',
            'tracking-tight text-text-primary',
            'transition-[border-color,background] duration-fast ease-signal',
            'focus:bg-ink/60 focus:outline-none focus:shadow-glow-soft',
            digit
              ? 'border-pink shadow-glow-soft'
              : 'border-white/[0.12] focus:border-pink',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      ))}
    </div>
  );
};
